import React, { createContext, ReactNode, useMemo } from 'react';
import { Provider as StateProvider } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';

import { ThemeProvider } from '@emotion/react';
import { StyleTheme } from '../../global';
import { UsePopup, usePopup } from '../../hooks/usePopup';
import {
    ApiProvider,
    FirebaseProvider,
    Settings,
    WindowProvider,
} from '../../providers';
import { Router } from '../../routes/Routes';
import { ApplicationStore, configureStore } from '../../state';

interface Props {
    settings: Settings;
    theme: StyleTheme;
    children?: ReactNode;
}

declare module '@emotion/react' {
    export interface Theme extends StyleTheme {
        version: Settings['languageVersion'];
    }

    export interface StyledProps {
        theme: Theme;
    }
}

interface ProviderProps {
    store: ApplicationStore;
    settings: Settings;
    dispatch: Dispatch<AnyAction>;
    popupComponent: UsePopup.PopupComponent;
    openPopup: UsePopup.Open;
    closePopup: UsePopup.Close;
}

/**
 * No need to provide default value which createContext expects. `undefined!`
 * (non-null assertion) is used to tell Typescript that, during runtime, the
 * defaultValue of context will not be null or undefined.
 */
export const CoreContext = createContext<ProviderProps>(undefined!);

export const CoreProvider = ({ settings, theme }: Props) => {
    const { closePopup, openPopup, popupComponent } = usePopup();
    const store = useMemo(() => configureStore(), []);

    const value: ProviderProps = useMemo(
        () => ({
            store,
            dispatch: store.dispatch,
            settings,
            closePopup,
            openPopup,
            popupComponent,
        }),
        [popupComponent, openPopup],
    );

    return (
        <StateProvider store={store}>
            <CoreContext.Provider value={value}>
                <FirebaseProvider>
                    <ApiProvider>
                        <WindowProvider>
                            <ThemeProvider
                                theme={{
                                    version: settings.languageVersion,
                                    ...theme,
                                }}
                            >
                                <div
                                    className={`layout-wrapper version--${settings.languageVersion}`}
                                >
                                    <Router />
                                </div>
                            </ThemeProvider>
                        </WindowProvider>
                    </ApiProvider>
                </FirebaseProvider>
            </CoreContext.Provider>
        </StateProvider>
    );
};

export * from './context';
